import { isNumber, truncate } from 'lodash';

import type {
  Config,
  PriceColumn,
  Sums,
} from '@flow/libs/data-validators/entity-schemas/document-box/product-table';

const PRICE_COL_TRUNCATE_LENGTH = 20;
const PRODUCT_COL_TRUNCATE_LENGTH = 40;

const isPriceColumn = (column: Config['columns'][number]): column is PriceColumn =>
  column.key.includes('price');

const getSumByPriceKey = (sums: Sums, key: keyof Sums, fallback: keyof Sums | number) => {
  if (isNumber(sums[key])) {
    return sums[key];
  }

  if (typeof fallback === 'string') {
    return sums[fallback];
  }

  return fallback;
};

const getFormattedPriceColumns = ({
  column,
  columns,
}: {
  column: PriceColumn;
  columns: Config['columns'];
}) => {
  const priceColumnLabel = column.label;
  const productColumnLabel = columns.find((c) => c.key === 'name')?.label || '';
  const truncatedPriceColumnLabel = truncate(priceColumnLabel, {
    length: PRICE_COL_TRUNCATE_LENGTH,
  });
  const truncatedProductColumnLabel = truncate(productColumnLabel, {
    length: PRODUCT_COL_TRUNCATE_LENGTH,
  });

  return {
    value: column.uuid,
    productLabel: truncatedProductColumnLabel,
    priceLabel: truncatedPriceColumnLabel,
  };
};

type Args = {
  boxId?: number;
  columns: Config['columns'];
  isBoxVisible?: boolean;
  sums: Sums;
};

export const getPriceColumns = ({ boxId, columns, isBoxVisible, sums }: Args) =>
  columns.filter(isPriceColumn).map((column) => {
    const priceKey = column.key as keyof Sums;
    const priceDiscountedKey = `${priceKey}_discounted` as keyof Sums;
    const priceInclTaxKey = `${priceKey}_incl_tax` as keyof Sums;
    const priceTax1AmountKey = `${priceKey}_tax_1_amount` as keyof Sums;
    const priceEffectiveUnitTax1AmountKey = `${priceKey}_effective_unit_tax_1_amount` as keyof Sums;

    return {
      visible: isBoxVisible,
      column: {
        ...column,
        boxId,
        price: sums[priceKey],
        price_discounted: getSumByPriceKey(sums, priceDiscountedKey, priceKey),
        price_incl_tax: getSumByPriceKey(sums, priceInclTaxKey, priceDiscountedKey),
        price_tax_1_amount: getSumByPriceKey(sums, priceTax1AmountKey, 0),
        price_effective_unit_tax_1_amount: getSumByPriceKey(
          sums,
          priceEffectiveUnitTax1AmountKey,
          0,
        ),
      },
      formatted: getFormattedPriceColumns({
        column,
        columns,
      }),
    };
  });
